import React, { useState, useEffect } from 'react';
import ReactGA from 'react-ga4';
import Clarity from '@microsoft/clarity';

const ReliableGoogleSignIn = ({ onSignIn, onSignOut, user }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Load Google Identity Services script
    if (!window.google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('✅ Google Identity Services loaded');
        initializeGoogleAuth();
      };
      document.head.appendChild(script);
    } else {
      initializeGoogleAuth();
    }
  }, []);

  const initializeGoogleAuth = () => {
    try {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '668797802841-aa89ubpb79400ml37anvc0uranpbdc1u.apps.googleusercontent.com',
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: false, // Disable FedCM to avoid compatibility issues
        });
        console.log('✅ Google Auth initialized successfully');
      }
    } catch (error) {
      console.error('❌ Error initializing Google Auth:', error);
      setError('Failed to initialize Google Sign-In');
    }
  };

  const handleCredentialResponse = (response) => {
    try {
      console.log('🔐 Google Sign-In response received');
      setIsLoading(true);
      setError(null);

      if (!response || !response.credential) {
        throw new Error('Invalid response from Google Sign-In');
      }

      // Decode the JWT token
      const token = response.credential;
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      const userData = JSON.parse(jsonPayload);
      console.log('👤 User data decoded:', { email: userData.email, name: userData.name });

      // Check if email domain is allowed
      const allowedDomains = ['adda247.com', 'addaeducation.com', 'studyiq.com'];
      const email = userData.email || '';
      const rawDomain = email.split('@')[1] || '';
      const userDomain = rawDomain.trim().toLowerCase();
      
      console.log('🔍 Domain check:', { email, rawDomain, userDomain, allowedDomains });

      if (!userDomain || !allowedDomains.includes(userDomain)) {
        console.error('❌ Domain not allowed:', { userDomain, email, allowedDomains });
        setError(`Access denied. Only users with emails from the following domains can sign in:
• adda247.com
• addaeducation.com
• studyiq.com

Your email domain: ${rawDomain || 'unknown'}`);
        
        // Track unauthorized access attempt
        ReactGA.event({
          category: 'Authentication',
          action: 'Unauthorized Domain Access',
          label: userDomain,
          value: 1
        });

        Clarity.event('unauthorized_domain_access');
        Clarity.setTag('blocked_domain', userDomain || rawDomain);
        return;
      }
      
      console.log('✅ Domain allowed:', userDomain);

      // Create user object
      const googleUser = {
        id: userData.sub,
        email: userData.email,
        name: userData.name,
        picture: userData.picture,
        provider: 'google',
        domain: userDomain,
        loginTime: new Date().toISOString(),
        token: token, // Store token for API calls
      };

      // Store user data
      localStorage.setItem('user', JSON.stringify(googleUser));

      // Track with Google Analytics
      ReactGA.event({
        category: 'Authentication',
        action: 'Google Sign In',
        label: 'Google OAuth',
        value: 1
      });

      ReactGA.set({
        userId: userData.sub,
        custom_map: {
          'user_type': 'google_user',
          'user_name': userData.name,
          'user_domain': userDomain
        }
      });

      // Track with Microsoft Clarity
      Clarity.identify(userData.sub, undefined, undefined, userData.name);
      Clarity.setTag('login_method', 'google');
      Clarity.setTag('user_type', 'google_user');
      Clarity.setTag('user_domain', userDomain);
      Clarity.event('google_signin_success');

      // Call the onSignIn callback
      if (onSignIn) {
        onSignIn(googleUser);
      }

      console.log('✅ Google Sign-In successful for domain:', userDomain);
    } catch (error) {
      console.error('❌ Error processing Google Sign-In response:', error);
      setError('Failed to process Google Sign-In. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    try {
      console.log('🔐 Initiating Google Sign-In...');
      setIsLoading(true);
      setError(null);

      if (window.google && window.google.accounts) {
        // Use renderButton method for better compatibility
        const buttonDiv = document.createElement('div');
        buttonDiv.style.display = 'none';
        document.body.appendChild(buttonDiv);

        window.google.accounts.id.renderButton(buttonDiv, {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          shape: 'rectangular',
          text: 'signin_with',
          logo_alignment: 'left',
          width: 300,
        });

        // Trigger click on the rendered button
        const button = buttonDiv.querySelector('div[role="button"]');
        if (button) {
          button.click();
        } else {
          // Fallback to prompt
          window.google.accounts.id.prompt();
        }

        // Clean up
        setTimeout(() => {
          document.body.removeChild(buttonDiv);
        }, 1000);
      } else {
        console.error('❌ Google Identity Services not loaded');
        setError('Google Sign-In is not available. Please refresh the page and try again.');
      }
    } catch (error) {
      console.error('❌ Error initiating Google Sign-In:', error);
      setError('Google Sign-In is not available. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    try {
      // Clear user data
      localStorage.removeItem('user');
      
      // Clear daily analysis counts
      const userKey = user?.id || user?.email;
      if (userKey) {
        const today = new Date().toDateString();
        localStorage.removeItem(`daily_video_analysis_${userKey}_${today}`);
        localStorage.removeItem(`daily_comment_analysis_${userKey}_${today}`);
      }

      // Track logout event
      ReactGA.event({
        category: 'Authentication',
        action: 'Logout',
        label: 'Reliable Auth',
        value: 1
      });

      Clarity.event('reliable_auth_logout');

      // Call parent logout handler
      if (onSignOut) {
        onSignOut();
      }

      console.log('✅ Logout successful');
    } catch (error) {
      console.error('❌ Error during logout:', error);
    }
  };

  return (
    <div className="reliable-auth-container">
      {error && (
        <div className="auth-error" style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1rem',
          color: '#fca5a5',
          fontSize: '0.9rem',
          whiteSpace: 'pre-line'
        }}>
          {error}
        </div>
      )}

      <button
        onClick={handleGoogleSignIn}
        disabled={isLoading}
        className="reliable-google-signin-button"
        style={{
          width: '100%',
          padding: '1rem 2rem',
          background: 'transparent',
          border: '2px solid rgba(139, 92, 246, 0.3)',
          borderRadius: '50px',
          backgroundImage: 'linear-gradient(135deg, #8b5cf6, #a855f7, #c084fc)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          fontWeight: '500',
          cursor: isLoading ? 'not-allowed' : 'pointer',
          fontSize: '1rem',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          fontFamily: 'inherit',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          opacity: isLoading ? 0.6 : 1
        }}
        onMouseOver={(e) => {
          if (!isLoading) {
            e.target.style.transform = 'scale(1.02) translateY(-2px)';
            e.target.style.borderColor = 'rgba(139, 92, 246, 0.5)';
            e.target.style.boxShadow = '0 0 20px rgba(139, 92, 246, 0.2)';
            e.target.style.WebkitBackgroundClip = 'text';
            e.target.style.backgroundClip = 'text';
            e.target.style.WebkitTextFillColor = 'transparent';
          }
        }}
        onMouseOut={(e) => {
          if (!isLoading) {
            e.target.style.transform = 'scale(1) translateY(0)';
            e.target.style.borderColor = 'rgba(139, 92, 246, 0.3)';
            e.target.style.boxShadow = 'none';
            e.target.style.WebkitBackgroundClip = 'text';
            e.target.style.backgroundClip = 'text';
            e.target.style.WebkitTextFillColor = 'transparent';
          }
        }}
      >
        {isLoading ? (
          <>
            <div className="spinner" style={{
              width: '20px',
              height: '20px',
              border: '2px solid rgba(139, 92, 246, 0.3)',
              borderTop: '2px solid #8b5cf6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            Signing in...
          </>
        ) : (
          <>
            <svg width="20" height="20" viewBox="0 0 24 24" style={{ fill: 'url(#googleGradient)' }}>
              <defs>
                <linearGradient id="googleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#8b5cf6" />
                  <stop offset="50%" stopColor="#a855f7" />
                  <stop offset="100%" stopColor="#c084fc" />
                </linearGradient>
              </defs>
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Sign in with Adda Education
          </>
        )}
      </button>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ReliableGoogleSignIn;
